import * as React from "react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { RadioGroup, RadioOption } from "@/components/ui/radio"

/**
 * Rehearsal or Live, for one setup (milestone 27).
 *
 * A setup starts in Rehearsal: it runs on its schedule and records who would
 * have been texted, and sends nothing. Choosing Live asks once, because it is
 * the one choice on this page that makes real texts go out. Whatever is picked
 * here, the union-wide switch under Admin → Delivery can still hold every setup
 * in rehearsal — never the reverse — and when it does, the screen says so.
 */
export function RehearsalMode({
  rehearsal,
  onChange,
  organizationName,
  globalRehearsal,
  latestRehearsalRunId,
  readOnly = false,
}: {
  rehearsal: boolean
  onChange: (rehearsal: boolean) => void
  organizationName: string
  /** True while the whole Portal is in rehearsal, whatever this setup says. */
  globalRehearsal: boolean
  latestRehearsalRunId: number | null
  readOnly?: boolean
}) {
  const [confirming, setConfirming] = React.useState(false)

  return (
    <section className="space-y-3">
      <div>
        <h2>Rehearsal or live</h2>
        <p className="mt-1 text-sm text-ink-muted">
          A rehearsal runs on schedule and records exactly who would have been texted,
          and what each of them would have received, but sends nothing. Review a
          rehearsal in History, then switch to Live when {organizationName} is ready.
        </p>
      </div>

      <RadioGroup aria-label="Rehearsal or live">
        <RadioOption
          name="rehearsal_mode"
          value="rehearsal"
          checked={rehearsal}
          disabled={readOnly}
          onChange={() => onChange(true)}
          label="Rehearsal"
          description="Record who would be texted. Nothing is sent."
        />
        <RadioOption
          name="rehearsal_mode"
          value="live"
          checked={!rehearsal}
          disabled={readOnly}
          onChange={() => setConfirming(true)}
          label="Live"
          description="Send the texts to members."
        />
      </RadioGroup>

      {latestRehearsalRunId !== null && (
        <p className="text-sm">
          <Link href={`/runs/${latestRehearsalRunId}`}>See the latest rehearsal</Link>
        </p>
      )}

      {!rehearsal && globalRehearsal && (
        <div className="callout callout-signal py-3" role="status">
          <p className="text-xs text-ink-body">
            This setup is set to Live, but the whole Portal is in Rehearsal under Admin
            → Delivery, so nothing will be sent until that is switched off too.
          </p>
        </div>
      )}

      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch to Live?</DialogTitle>
            <DialogDescription>
              From its next run after you save, this will text real members in{" "}
              {organizationName}. Make sure a rehearsal has recorded what you expect.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Stay in Rehearsal
              </Button>
            </DialogClose>
            <Button
              type="button"
              onClick={() => {
                onChange(false)
                setConfirming(false)
              }}
            >
              Switch to Live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
